import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import Layout from "@/components/layout/Layout";
import Analytics from "@/components/analytics/Analytics";

// Code-split pages
const Home = lazy(() => import("@/pages/Home"));
const Services = lazy(() => import("@/pages/Services"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const PostList = lazy(() => import("@/pages/admin/PostList"));
const PostEditor = lazy(() => import("@/pages/admin/PostEditor"));

function Loading() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/admin" replace />;
  return children;
}

function NotFound() {
  return (
    <div data-testid="not-found-page" className="py-32 text-center px-6">
      <h1 className="text-6xl font-bold text-primary mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>404</h1>
      <p className="text-lg text-muted-foreground mb-8">Page not found</p>
      <a href="/" className="text-primary hover:underline font-medium">Back to home</a>
    </div>
  );
}

function AppRoutes() {
  return (
    <Suspense fallback={<Loading />}>
      <Analytics />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/services" element={<Services />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/legal/:slug" element={<LegalPage />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/posts" element={<ProtectedRoute><PostList /></ProtectedRoute>} />
        <Route path="/admin/posts/new" element={<ProtectedRoute><PostEditor /></ProtectedRoute>} />
        <Route path="/admin/posts/edit/:slug" element={<ProtectedRoute><PostEditor /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider attribute="class" defaultTheme="dark" storageKey="tb-theme">
        <AuthProvider>
          <BrowserRouter>
            <Layout>
              <AppRoutes />
            </Layout>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
